import { zodResolver } from '@hookform/resolvers/zod';
import {
  useGetApiIntegrationsEosHouseholds,
  useGetApiIntegrationsEosProviders,
  usePostApiIntegrationsEosHouseholds,
  useDeleteApiIntegrationsEosHouseholdsId,
  usePutApiIntegrationsEosHouseholdsId,
  usePostApiIntegrationsEosHouseholdsIdSync,
  useGetApiIntegrationsEosHouseholdsIdStbs,
  usePostApiIntegrationsEosStbsIdEnable,
  usePostApiIntegrationsEosStbsIdDisable,
  usePutApiIntegrationsEosStbsId,
  getGetApiIntegrationsEosHouseholdsQueryKey,
  getGetApiIntegrationsEosHouseholdsIdStbsQueryKey,
  type EosHousehold,
  type EosSTB,
  type EosPowerState,
} from '@smarthome/api';
import { ROUTES } from '@smarthome/ui';
import { useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  Check,
  AlertCircle,
  ArrowLeft,
  Plus,
  Tv,
  Pencil,
  Trash2,
  RefreshCw,
  ToggleRight,
  ToggleLeft,
} from 'lucide-react';
import { useState, useRef, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { Button, Modal, TextField, cn } from '@/components/ui';

const addHouseholdSchema = z.object({
  provider: z.string().min(1, 'Provider is required'),
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});

const updateHouseholdSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
});

const enableSTBSchema = z.object({
  humanId: z
    .string()
    .min(1, 'ID is required')
    .regex(/^[a-z0-9.-]+$/, 'ID must be lowercase letters, numbers, hyphens, and periods only'),
});

const updateSTBSchema = z.object({
  displayName: z.string().optional(),
});

type AddHouseholdFormData = z.infer<typeof addHouseholdSchema>;
type UpdateHouseholdFormData = z.infer<typeof updateHouseholdSchema>;
type EnableSTBFormData = z.infer<typeof enableSTBSchema>;
type UpdateSTBFormData = z.infer<typeof updateSTBSchema>;

export function EosSettingsPage() {
  const queryClient = useQueryClient();
  const {
    data: householdsData,
    isLoading,
    isError,
    refetch,
  } = useGetApiIntegrationsEosHouseholds();
  const { data: providersData } = useGetApiIntegrationsEosProviders();

  const [showAddModal, setShowAddModal] = useState(false);
  const [editingHousehold, setEditingHousehold] = useState<EosHousehold | null>(null);
  const [deletingHousehold, setDeletingHousehold] = useState<EosHousehold | null>(null);
  const [expandedHousehold, setExpandedHousehold] = useState<string | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const addMutation = usePostApiIntegrationsEosHouseholds();
  const updateMutation = usePutApiIntegrationsEosHouseholdsId();
  const deleteMutation = useDeleteApiIntegrationsEosHouseholdsId();
  const syncMutation = usePostApiIntegrationsEosHouseholdsIdSync();

  const households = householdsData?.households ?? [];
  const providers = providersData?.providers ?? [];

  const invalidateHouseholds = () => {
    queryClient.invalidateQueries({ queryKey: getGetApiIntegrationsEosHouseholdsQueryKey() });
  };

  const handleSync = async (household: EosHousehold) => {
    if (!household.id) return;
    try {
      await syncMutation.mutateAsync({ id: household.id });
      invalidateHouseholds();
      if (expandedHousehold === household.id) {
        queryClient.invalidateQueries({
          queryKey: getGetApiIntegrationsEosHouseholdsIdStbsQueryKey(household.id),
        });
      }
    } catch {
      // Error handling done by mutation
    }
  };

  return (
    <div className="space-y-8">
      {/* Header with back link */}
      <div>
        <Link
          to={ROUTES.SETTINGS}
          className="mb-4 inline-flex items-center gap-2 text-sm text-gray-500 transition-colors hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Settings
        </Link>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
              EOS Set-Top Boxes
            </h1>
            <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
              Manage your EOS provider accounts and set-top boxes
            </p>
          </div>
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setShowAddModal(true)}>
            Add Account
          </Button>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/10 p-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div>
              <h3 className="font-medium text-red-400">Unable to fetch accounts</h3>
              <p className="mt-1 text-sm text-red-400/70">
                There was an error loading your EOS accounts.
              </p>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm font-medium text-red-400 transition-colors hover:bg-red-500/20"
          >
            Retry
          </button>
        </div>
      )}

      {/* Household List */}
      {!isLoading && !isError && (
        <>
          {households.length === 0 ? (
            <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-white/[0.06] dark:bg-[#18181b]">
              <Tv className="mx-auto h-12 w-12 text-gray-400 dark:text-zinc-600" />
              <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
                No accounts configured
              </h3>
              <p className="mt-2 text-sm text-gray-500 dark:text-zinc-500">
                Add your first EOS provider account to get started
              </p>
              <Button
                className="mt-6"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => setShowAddModal(true)}
              >
                Add Account
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <h2 className="text-sm font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
                Provider Accounts
              </h2>
              <div className="space-y-4">
                {households.map((household) => (
                  <HouseholdCard
                    key={household.id}
                    household={household}
                    isExpanded={expandedHousehold === household.id}
                    onToggleExpand={() =>
                      setExpandedHousehold(
                        expandedHousehold === household.id ? null : (household.id ?? null)
                      )
                    }
                    onEdit={() => setEditingHousehold(household)}
                    onDelete={() => setDeletingHousehold(household)}
                    onSync={() => handleSync(household)}
                    isSyncing={
                      syncMutation.isPending && syncMutation.variables?.id === household.id
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Add Household Modal */}
      <AddHouseholdModal
        isOpen={showAddModal}
        providers={providers}
        onClose={() => {
          setShowAddModal(false);
          setAddError(null);
        }}
        onSubmit={async (data) => {
          setAddError(null);
          try {
            await addMutation.mutateAsync({ data });
            setShowAddModal(false);
            invalidateHouseholds();
          } catch (err) {
            const error = err as { code?: string; message?: string };
            if (error.code === 'HOUSEHOLD_ALREADY_EXISTS') {
              setAddError('An account with this username already exists for this provider.');
            } else if (error.code === 'INVALID_PROVIDER') {
              setAddError('Invalid provider selected.');
            } else if (error.code === 'INVALID_CREDENTIALS') {
              setAddError('Invalid username or password.');
            } else {
              setAddError('Failed to add account. Please try again.');
            }
          }
        }}
        isLoading={addMutation.isPending}
        error={addError}
      />

      {/* Edit Household Modal */}
      {editingHousehold && (
        <EditHouseholdModal
          isOpen={true}
          household={editingHousehold}
          onClose={() => {
            setEditingHousehold(null);
            setEditError(null);
          }}
          onSubmit={async (data) => {
            if (!editingHousehold.id) return;
            setEditError(null);
            try {
              await updateMutation.mutateAsync({ id: editingHousehold.id, data });
              setEditingHousehold(null);
              invalidateHouseholds();
            } catch (err) {
              const error = err as { code?: string; message?: string };
              if (error.code === 'INVALID_CREDENTIALS') {
                setEditError('Invalid username or password.');
              } else {
                setEditError('Failed to update account. Please try again.');
              }
            }
          }}
          isLoading={updateMutation.isPending}
          error={editError}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingHousehold && (
        <DeleteHouseholdModal
          isOpen={true}
          household={deletingHousehold}
          onClose={() => setDeletingHousehold(null)}
          onConfirm={async () => {
            if (!deletingHousehold.id) return;
            try {
              await deleteMutation.mutateAsync({ id: deletingHousehold.id });
              setDeletingHousehold(null);
              invalidateHouseholds();
            } catch {
              // Error handling done by mutation
            }
          }}
          isLoading={deleteMutation.isPending}
        />
      )}
    </div>
  );
}

interface HouseholdCardProps {
  household: EosHousehold;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onSync: () => void;
  isSyncing: boolean;
}

function HouseholdCard({
  household,
  isExpanded,
  onToggleExpand,
  onEdit,
  onDelete,
  onSync,
  isSyncing,
}: HouseholdCardProps) {
  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
      {/* Household Header */}
      <div className="flex items-center justify-between p-4">
        <button onClick={onToggleExpand} className="flex flex-1 items-center gap-4 text-left">
          <div
            className={`h-3 w-3 rounded-full ${household.connected ? 'bg-emerald-400 shadow-lg shadow-emerald-400/30' : 'bg-gray-400 dark:bg-zinc-600'}`}
          />
          <div className="flex-1">
            <div className="text-[15px] font-medium text-gray-900 dark:text-white">
              {household.provider}
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-sm text-gray-500 dark:text-zinc-500">
              <span>{household.username}</span>
              <span className="text-gray-300 dark:text-zinc-700">•</span>
              <span>
                {household.stbCount ?? 0} STB{(household.stbCount ?? 0) !== 1 ? 's' : ''}
              </span>
              <span className="text-gray-300 dark:text-zinc-700">•</span>
              <span>{household.connected ? 'Connected' : 'Disconnected'}</span>
            </div>
          </div>
          <ChevronDown
            className={`h-5 w-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
          />
        </button>
        <div className="ml-4 flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onSync} disabled={isSyncing} title="Sync STBs">
            {isSyncing ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
          </Button>
          <Button variant="ghost" size="sm" onClick={onEdit}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      </div>

      {/* STB List (expanded) */}
      {isExpanded && household.id && <STBList householdId={household.id} />}
    </div>
  );
}

interface STBListProps {
  householdId: string;
}

function STBList({ householdId }: STBListProps) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useGetApiIntegrationsEosHouseholdsIdStbs(householdId);

  const [enablingSTB, setEnablingSTB] = useState<EosSTB | null>(null);
  const [editingSTB, setEditingSTB] = useState<EosSTB | null>(null);
  const [enableError, setEnableError] = useState<string | null>(null);
  const [editError, setEditError] = useState<string | null>(null);

  const enableMutation = usePostApiIntegrationsEosStbsIdEnable();
  const disableMutation = usePostApiIntegrationsEosStbsIdDisable();
  const updateMutation = usePutApiIntegrationsEosStbsId();

  const stbs = data?.stbs ?? [];

  const invalidateSTBs = () => {
    queryClient.invalidateQueries({
      queryKey: getGetApiIntegrationsEosHouseholdsIdStbsQueryKey(householdId),
    });
    queryClient.invalidateQueries({ queryKey: getGetApiIntegrationsEosHouseholdsQueryKey() });
  };

  const handleDisable = async (stb: EosSTB) => {
    if (!stb.id) return;
    try {
      await disableMutation.mutateAsync({ id: stb.id });
      invalidateSTBs();
    } catch {
      // Error handling done by mutation
    }
  };

  if (isLoading) {
    return (
      <div className="border-t border-gray-200 p-4 dark:border-white/[0.06]">
        <div className="flex items-center justify-center py-4">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="border-t border-gray-200 p-4 dark:border-white/[0.06]">
        <p className="text-sm text-red-400">Failed to load STBs</p>
      </div>
    );
  }

  if (stbs.length === 0) {
    return (
      <div className="border-t border-gray-200 p-4 dark:border-white/[0.06]">
        <p className="text-sm text-gray-500 dark:text-zinc-500">
          No set-top boxes found. Try syncing the account.
        </p>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-200 dark:border-white/[0.06]">
      <div className="bg-gray-50 px-4 py-2 dark:bg-zinc-900/50">
        <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
          Set-Top Boxes
        </h3>
      </div>
      {stbs.map((stb, index) => (
        <STBListItem
          key={stb.id}
          stb={stb}
          isFirst={index === 0}
          onEnable={() => setEnablingSTB(stb)}
          onDisable={() => handleDisable(stb)}
          onEdit={() => setEditingSTB(stb)}
          isDisabling={disableMutation.isPending && disableMutation.variables?.id === stb.id}
        />
      ))}

      {/* Enable STB Modal */}
      {enablingSTB && (
        <EnableSTBModal
          isOpen={true}
          stb={enablingSTB}
          onClose={() => {
            setEnablingSTB(null);
            setEnableError(null);
          }}
          onSubmit={async (data) => {
            if (!enablingSTB.id) return;
            setEnableError(null);
            try {
              await enableMutation.mutateAsync({ id: enablingSTB.id, data });
              setEnablingSTB(null);
              invalidateSTBs();
            } catch (err) {
              const error = err as { code?: string; message?: string };
              if (error.code === 'CONFLICT') {
                setEnableError('This ID is already in use by another device.');
              } else {
                setEnableError('Failed to enable STB. Please try again.');
              }
            }
          }}
          isLoading={enableMutation.isPending}
          error={enableError}
        />
      )}

      {/* Edit STB Modal */}
      {editingSTB && (
        <EditSTBModal
          isOpen={true}
          stb={editingSTB}
          onClose={() => {
            setEditingSTB(null);
            setEditError(null);
          }}
          onSubmit={async (data) => {
            if (!editingSTB.id) return;
            setEditError(null);
            try {
              await updateMutation.mutateAsync({ id: editingSTB.id, data });
              setEditingSTB(null);
              invalidateSTBs();
            } catch {
              setEditError('Failed to update STB. Please try again.');
            }
          }}
          isLoading={updateMutation.isPending}
          error={editError}
        />
      )}
    </div>
  );
}

interface STBListItemProps {
  stb: EosSTB;
  isFirst: boolean;
  onEnable: () => void;
  onDisable: () => void;
  onEdit: () => void;
  isDisabling: boolean;
}

function STBListItem({ stb, isFirst, onEnable, onDisable, onEdit, isDisabling }: STBListItemProps) {
  const powerState = stb.connection?.powerState as EosPowerState | undefined;

  const getStatusColor = () => {
    if (!stb.enabled) return 'bg-gray-400 dark:bg-zinc-600';
    switch (powerState) {
      case 'on':
        return 'bg-emerald-400 shadow-lg shadow-emerald-400/30';
      case 'standby':
        return 'bg-amber-400 shadow-lg shadow-amber-400/30';
      case 'offline':
        return 'bg-red-400 shadow-lg shadow-red-400/30';
      default:
        return 'bg-gray-400 dark:bg-zinc-600';
    }
  };

  const getStatusText = () => {
    if (!stb.enabled) return 'Disabled';
    switch (powerState) {
      case 'on':
        return 'On';
      case 'standby':
        return 'Standby';
      case 'offline':
        return 'Offline';
      default:
        return 'Unknown';
    }
  };

  // Use displayName if set, otherwise fall back to provider name
  const displayName = stb.displayName || stb.name;

  return (
    <div
      className={`flex items-center justify-between px-4 py-3 ${
        !isFirst ? 'border-t border-gray-100 dark:border-white/[0.04]' : ''
      }`}
    >
      <div className="flex items-center gap-4">
        <div className={`h-2.5 w-2.5 rounded-full ${getStatusColor()}`} />
        <div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-white">{displayName}</span>
            {stb.displayName && stb.displayName !== stb.name && (
              <span className="text-xs text-gray-400 dark:text-zinc-600">({stb.name})</span>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-500">
            <span>{getStatusText()}</span>
            {stb.humanId && (
              <>
                <span className="text-gray-300 dark:text-zinc-700">•</span>
                <span className="font-mono">{stb.humanId}</span>
              </>
            )}
            {stb.serialNumber && (
              <>
                <span className="text-gray-300 dark:text-zinc-700">•</span>
                <span className="font-mono">{stb.serialNumber}</span>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {stb.enabled && (
          <Button variant="ghost" size="sm" onClick={onEdit} title="Edit display name">
            <Pencil className="h-4 w-4" />
          </Button>
        )}
        {stb.enabled ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={onDisable}
            disabled={isDisabling}
            title="Disable STB"
          >
            {isDisabling ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600" />
            ) : (
              <ToggleRight className="h-4 w-4 text-emerald-500" />
            )}
          </Button>
        ) : (
          <Button variant="ghost" size="sm" onClick={onEnable} title="Enable STB">
            <ToggleLeft className="h-4 w-4 text-gray-400" />
          </Button>
        )}
      </div>
    </div>
  );
}

// Custom dropdown component for consistent styling
interface DropdownSelectProps {
  label: string;
  value: string | null;
  onChange: (value: string | null) => void;
  options: { value: string; label: string; sublabel?: string }[];
  placeholder?: string;
  disabled?: boolean;
  error?: string;
}

function DropdownSelect({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select...',
  disabled,
  error,
}: DropdownSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
        {label}
      </label>
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={cn(
          'flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left text-sm transition-all',
          'bg-white dark:bg-[#111113]',
          disabled && 'cursor-not-allowed opacity-50',
          error
            ? 'border-red-500 focus:ring-red-500'
            : 'border-gray-200 hover:border-gray-300 dark:border-white/[0.08] dark:hover:border-white/[0.12]',
          isOpen && !error && 'border-blue-500 ring-1 ring-blue-500'
        )}
      >
        <span
          className={
            selectedOption ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-zinc-500'
          }
        >
          {selectedOption?.label ?? placeholder}
        </span>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-gray-400 transition-transform dark:text-zinc-500',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1.5 w-full rounded-lg border border-gray-200 bg-white py-1 shadow-xl dark:border-white/[0.08] dark:bg-[#1c1c1f]">
          <div className="max-h-56 overflow-y-auto">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => {
                  onChange(option.value || null);
                  setIsOpen(false);
                }}
                className={cn(
                  'flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors',
                  'hover:bg-gray-50 dark:hover:bg-white/[0.04]',
                  value === option.value && 'bg-blue-50 dark:bg-blue-500/10'
                )}
              >
                <div>
                  <div
                    className={cn(
                      'text-gray-900 dark:text-white',
                      value === option.value && 'text-blue-600 dark:text-blue-400'
                    )}
                  >
                    {option.label}
                  </div>
                  {option.sublabel && (
                    <div className="text-xs text-gray-500 dark:text-zinc-500">
                      {option.sublabel}
                    </div>
                  )}
                </div>
                {value === option.value && (
                  <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {error && (
        <p className="mt-1.5 flex items-center gap-1 text-sm text-red-600 dark:text-red-400">
          <AlertCircle className="h-3.5 w-3.5" />
          {error}
        </p>
      )}
    </div>
  );
}

interface AddHouseholdModalProps {
  isOpen: boolean;
  providers: string[];
  onClose: () => void;
  onSubmit: (data: AddHouseholdFormData) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

function AddHouseholdModal({
  isOpen,
  providers,
  onClose,
  onSubmit,
  isLoading,
  error,
}: AddHouseholdModalProps) {
  const {
    handleSubmit,
    reset,
    control,
    register,
    formState: { errors },
  } = useForm<AddHouseholdFormData>({
    resolver: zodResolver(addHouseholdSchema),
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const providerOptions = useMemo(
    () => providers.map((p) => ({ value: p, label: p })),
    [providers]
  );

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="add-household-form" loading={isLoading}>
        Add Account
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Add EOS Account"
      subtitle="Connect your TV provider account"
      size="md"
      footer={footerContent}
    >
      <form id="add-household-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Controller
          name="provider"
          control={control}
          render={({ field: { value, onChange } }) => (
            <DropdownSelect
              label="Provider"
              value={value ?? null}
              onChange={(v) => onChange(v ?? '')}
              options={providerOptions}
              placeholder="Select a provider"
              error={errors.provider?.message}
            />
          )}
        />

        <TextField
          label="Username"
          placeholder="your@email.com"
          error={errors.username?.message}
          {...register('username')}
        />

        <TextField
          label="Password"
          type="password"
          placeholder="••••••••"
          error={errors.password?.message}
          {...register('password')}
        />

        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

interface EditHouseholdModalProps {
  isOpen: boolean;
  household: EosHousehold;
  onClose: () => void;
  onSubmit: (data: UpdateHouseholdFormData) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

function EditHouseholdModal({
  isOpen,
  household,
  onClose,
  onSubmit,
  isLoading,
  error,
}: EditHouseholdModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdateHouseholdFormData>({
    resolver: zodResolver(updateHouseholdSchema),
    defaultValues: {
      username: household.username ?? '',
      password: '',
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="edit-household-form" loading={isLoading}>
        Save Changes
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Account"
      subtitle="Update your provider credentials"
      size="md"
      footer={footerContent}
    >
      <form id="edit-household-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
            Provider
          </label>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-500 dark:border-white/[0.06] dark:bg-zinc-900 dark:text-zinc-500">
            {household.provider}
          </div>
        </div>

        <TextField
          label="Username"
          placeholder="your@email.com"
          error={errors.username?.message}
          {...register('username')}
        />

        <TextField
          label="Password"
          type="password"
          placeholder="••••••••"
          error={errors.password?.message}
          {...register('password')}
        />

        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

interface DeleteHouseholdModalProps {
  isOpen: boolean;
  household: EosHousehold;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  isLoading: boolean;
}

function DeleteHouseholdModal({
  isOpen,
  household,
  onClose,
  onConfirm,
  isLoading,
}: DeleteHouseholdModalProps) {
  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={onClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="button" variant="danger" onClick={onConfirm} loading={isLoading}>
        Delete
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Delete Account"
      subtitle="This action cannot be undone"
      size="sm"
      footer={footerContent}
    >
      <p className="text-sm text-gray-600 dark:text-zinc-400">
        Are you sure you want to delete the{' '}
        <strong className="text-gray-900 dark:text-white">{household.provider}</strong> account for{' '}
        <strong className="text-gray-900 dark:text-white">{household.username}</strong>? This will
        also remove all associated set-top boxes.
      </p>
    </Modal>
  );
}

interface EnableSTBModalProps {
  isOpen: boolean;
  stb: EosSTB;
  onClose: () => void;
  onSubmit: (data: EnableSTBFormData) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

function EnableSTBModal({ isOpen, stb, onClose, onSubmit, isLoading, error }: EnableSTBModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EnableSTBFormData>({
    resolver: zodResolver(enableSTBSchema),
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="enable-stb-form" loading={isLoading}>
        Enable
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Enable Set-Top Box"
      subtitle={`Assign an identifier to ${stb.name}`}
      size="md"
      footer={footerContent}
    >
      <form id="enable-stb-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <TextField
          label="ID"
          placeholder="living-room-stb"
          error={errors.humanId?.message}
          {...register('humanId')}
        />

        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

interface EditSTBModalProps {
  isOpen: boolean;
  stb: EosSTB;
  onClose: () => void;
  onSubmit: (data: UpdateSTBFormData) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

function EditSTBModal({ isOpen, stb, onClose, onSubmit, isLoading, error }: EditSTBModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdateSTBFormData>({
    resolver: zodResolver(updateSTBSchema),
    defaultValues: {
      displayName: stb.displayName ?? '',
    },
  });

  // Reset form when stb changes
  useEffect(() => {
    reset({ displayName: stb.displayName ?? '' });
  }, [stb, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const footerContent = (
    <>
      <Button type="button" variant="ghost" onClick={handleClose} disabled={isLoading}>
        Cancel
      </Button>
      <Button type="submit" form="edit-stb-form" loading={isLoading}>
        Save Changes
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Set-Top Box"
      subtitle={`Customize display name for ${stb.name}`}
      size="md"
      footer={footerContent}
    >
      <form id="edit-stb-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-zinc-300">
            Provider Name
          </label>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-500 dark:border-white/[0.06] dark:bg-zinc-900 dark:text-zinc-500">
            {stb.name}
          </div>
        </div>

        <TextField
          label="Display Name"
          placeholder="e.g., Living Room TV"
          error={errors.displayName?.message}
          {...register('displayName')}
        />

        <p className="text-xs text-gray-500 dark:text-zinc-500">
          Leave empty to use the provider name. The display name will be shown in the device list
          and controls.
        </p>

        {error && (
          <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}
